import { InputNumber, Slider, type SliderSingleProps } from 'antd';
import { useCallback } from 'react';
import styled from 'styled-components';
import { useInputController } from '../hooks/useInputController.ts';
import { useFormItemAccessibility } from '../item/useFormItemAccessibility.tsx';
import { useFormItemEnabled } from '../item/useFormItemEnabled.ts';

const SliderNumberWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export interface ISliderNumberInputProps
  extends Omit<
    SliderSingleProps,
    'value' | 'onChange' | 'range' | 'defaultValue'
  > {
  name: string;
  /** Cifre decimali per l'arrotondamento del valore emesso. 0 = intero. */
  precision?: number;
  /**
   * Se `true` emette il valore come stringa invece che come numero.
   * Default: numero.
   */
  asString?: boolean;
  /** Mostra il campo numerico affiancato allo slider (default: true). */
  showInput?: boolean;
  /** Unità di misura mostrata come suffisso nel campo numerico (es. "px"). */
  unit?: string;
  /** Testo segnaposto del campo numerico (es. il valore di default). */
  placeholder?: string;
}

/**
 * Campo numerico basato su uno slider trascinabile, affiancato (di default)
 * da un campo numerico sincronizzato per l'inserimento preciso.
 *
 * Adatto a valori vincolati in un intervallo (es. opacità 0-1). Supporta sia
 * interi (`precision={0}`) sia decimali (`precision={n}`), e può emettere il
 * valore come numero (default) o come stringa (`asString`).
 */
export function SliderNumberInput(props: ISliderNumberInputProps) {
  const {
    name,
    precision,
    asString,
    showInput = true,
    unit,
    placeholder,
    min,
    max,
    step,
    style,
    ...sliderProps
  } = props;

  const { enabled, visuallyDisabled } = useFormItemEnabled();

  const {
    field: { value, onChange, onBlur, ref },
  } = useInputController(name, { enabled });

  const accessibility = useFormItemAccessibility();

  // Il valore nel form state può essere una stringa (es. dati salvati in
  // precedenza da DecimalInput): normalizzo sempre a numero per gli input antd.
  const numericValue =
    value == null || value === '' ? undefined : Number(value);

  const handleChange = useCallback(
    (next: number | null) => {
      if (next == null) {
        onChange(null);
        return;
      }
      const rounded =
        precision != null ? Number(next.toFixed(precision)) : next;
      onChange(asString ? String(rounded) : rounded);
    },
    [asString, precision, onChange],
  );

  if (!showInput) {
    return (
      <Slider
        id={accessibility.labelId}
        {...sliderProps}
        ref={ref}
        min={min}
        max={max}
        step={step}
        value={numericValue}
        onChange={handleChange}
        onBlur={onBlur}
        disabled={visuallyDisabled}
        style={style}
      />
    );
  }

  return (
    <SliderNumberWrapper style={style}>
      <Slider
        {...sliderProps}
        min={min}
        max={max}
        step={step}
        value={numericValue}
        onChange={handleChange}
        disabled={visuallyDisabled}
        style={{ flex: 1 }}
      />
      <InputNumber
        {...accessibility.attributes}
        ref={ref}
        name={name}
        size="large"
        min={min}
        max={max}
        step={step ?? undefined}
        precision={precision}
        decimalSeparator=","
        suffix={unit}
        placeholder={placeholder}
        value={numericValue}
        onChange={handleChange}
        onBlur={onBlur}
        disabled={visuallyDisabled}
        style={{ width: unit ? 112 : 96 }}
      />
    </SliderNumberWrapper>
  );
}
